import React, { useState } from "react";
import { Link } from "react-router-dom";
import technicalFacilityGranulation from "../../images/technical_facility_granulation.png";
import technicalProcessingInstruments from "../../images/technical_processing_instruments.png";
import technicalStabilityChambers from "../../images/technical_stability_chambers.png";

export default function TechnicalServicesRedesign() {
  const [activeTab, setActiveTab] = useState("facility");

  const coreWays = [
    {
      title: "Complete Product & Dossier Development",
      description: "End-to-end development starting from molecule characterization all the way to complete regulatory dossier submission.",
      icon: (
        // Clipboard list Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 00 2 2h10a2 2 0 00 2-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      )
    },
    {
      title: "Facility Usage & Services",
      description: "Dedicated access to state-of-the-art instruments, pilot lines, and clinical manufacturing facilities at our ViRACS centre.",
      icon: (
        // Home/Facility Icon
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      )
    }
  ];

  const additionalSupport = [
    {
      title: "Commercial Troubleshooting",
      desc: "Investigating and resolving stability, blending, dissolution, or compression failures in existing commercial batches."
    },
    {
      title: "Formulation Optimization",
      desc: "Evaluating new raw materials or alternative excipients to optimize performance, yield, and project economics."
    },
    {
      title: "Technology Transfer",
      desc: "Robust scaling and transfer of products from laboratory R&D environments to commercial production facilities."
    },
    {
      title: "Process Scale-Up",
      desc: "Systematic evaluation of batch size increases, process parameter validation, and product quality assessments."
    },
    {
      title: "Process Modernization",
      desc: "Upgrading legacy manufacturing lines using time & cost saving approaches like Direct Compression, Twin-Screw Granulation, and Continuous Manufacturing."
    }
  ];

  return (
    <div className="bg-[#F7F9FA] min-h-screen text-slate-800">
      {/* Hero Header */}
      <section className="relative min-h-[40vh] flex items-center justify-center pt-32 pb-16 overflow-hidden bg-brand-blue-deep">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 mix-blend-overlay"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1579165466541-71702b859ec1?q=80&w=1920&auto=format&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-blue-deep via-brand-blue/90 to-brand-blue/30" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 text-center text-white z-10">
          <div className="inline-flex items-center space-x-2 bg-brand-teal/20 border border-brand-teal/30 rounded-full px-4 py-1.5 mb-4">
            <span className="text-brand-teal-light font-semibold text-xs uppercase tracking-wider">
              Technical Capabilities
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Technical &amp; Facility Services
          </h1>
          <p className="max-w-2xl mx-auto text-base md:text-lg text-slate-300 font-light leading-relaxed">
            State-of-the-art laboratory instrumentation, stability testing, and process modernization supporting our partners from initial R&amp;D through commercial scale transfer.
          </p>
        </div>
      </section>

      {/* 1. Core Partnerships Section */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full mb-3">
            Partnership Models
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue tracking-tight">
            We Support Our Partners in Two Key Ways
          </h2>
          <p className="text-slate-500 font-light mt-3">
            Flexible collaboration structures designed to adapt to your organization's specific technical scale and requirements.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {coreWays.map((way, idx) => (
            <div 
              key={idx}
              className="bg-white border border-slate-100 hover:border-brand-teal/25 rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 transform hover:-translate-y-1 flex items-start space-x-6"
            >
              <div className="w-14 h-14 rounded-2xl bg-brand-teal-soft flex items-center justify-center text-brand-teal shrink-0">
                {way.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold text-brand-blue mb-2">{way.title}</h3>
                <p className="text-slate-600 font-light text-sm md:text-base leading-relaxed">{way.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 2. Interactive Facility Showcase (PowerPoint Slides Redesign) */}
      <section className="py-20 bg-slate-50 border-t border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full mb-3">
              Showcase
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-brand-blue tracking-tight">
              Facility, Instrumentation &amp; Storage
            </h2>
            <p className="text-slate-500 font-light mt-3">
              Explore our ViRACS innovation centre capabilities across solid dosage processing, novel drug delivery instruments, and environment-controlled stability storage.
            </p>
          </div>

          {/* Interactive Tabs */}
          <div className="flex justify-center mb-12">
            <div className="bg-white border border-slate-200/60 p-1.5 rounded-full shadow-sm flex space-x-1">
              <button 
                onClick={() => setActiveTab("facility")}
                className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "facility" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
              >
                Formulation Facility
              </button>
              <button 
                onClick={() => setActiveTab("instruments")}
                className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "instruments" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
              >
                Processing Instruments
              </button>
              <button 
                onClick={() => setActiveTab("stability")}
                className={`px-5 py-2 rounded-full text-xs md:text-sm font-semibold transition-all ${activeTab === "stability" ? "bg-brand-blue text-white" : "text-slate-600 hover:text-brand-blue"}`}
              >
                Stability &amp; Storage
              </button>
            </div>
          </div>

          {/* Tab Content Area */}
          <div className="bg-white border border-slate-100 rounded-3xl p-8 md:p-12 shadow-md relative min-h-[400px]">
            
            {/* Tab 1: Formulation Support Facility */}
            {activeTab === "facility" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 animate-fade-in">
                <div className="lg:col-span-7 space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <div className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                      Solid Dosage Support
                    </div>
                    <h3 className="text-2xl font-bold text-brand-blue">We Provide Formulation Support With Facility For Development</h3>
                    <p className="text-slate-500 font-light text-sm mt-1">High-performance machinery supporting flexible pilot and R&amp;D solid dosage runs.</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <span className="text-xs font-bold text-brand-teal">Granulation</span>
                      <h4 className="text-lg font-bold text-brand-blue mt-1">High Shear Mixer (2/5/10 Ltr)</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                        Equipped for high-efficiency wet granulation, raw material binder mixing, and variable volume formulations.
                      </p>
                    </div>
                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <span className="text-xs font-bold text-brand-teal">Drying &amp; Processing</span>
                      <h4 className="text-lg font-bold text-brand-blue mt-1">Fluid Bed Processor</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                        Supports fluid-bed drying, top-spray granulation, and custom particle coating with advanced airflow diagnostics.
                      </p>
                    </div>
                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <span className="text-xs font-bold text-brand-teal">Film Coating</span>
                      <h4 className="text-lg font-bold text-brand-blue mt-1">Auto Coater</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                        Precision automated tablet coater ensuring uniform film application, controlled exhaust air, and spray rate stability.
                      </p>
                    </div>
                    <div className="bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <span className="text-xs font-bold text-brand-teal">Compression</span>
                      <h4 className="text-lg font-bold text-brand-blue mt-1">B and D Tooling Compression</h4>
                      <p className="text-slate-600 font-light text-xs md:text-sm mt-2 leading-relaxed">
                        Tablet tooling setups supporting standard B and D die configurations, enabling custom punch forces and shapes.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="relative group overflow-hidden rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                    <img 
                      src={technicalFacilityGranulation} 
                      alt="Formulation Support Facility" 
                      className="w-full h-[320px] lg:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-85" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider">Solid Dosage Line</p>
                      <h4 className="text-sm font-bold mt-0.5">Formulation Support Facility</h4>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: Processing Instruments */}
            {activeTab === "instruments" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 animate-fade-in">
                <div className="lg:col-span-7 space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <div className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                      Novel Drug Delivery
                    </div>
                    <h3 className="text-2xl font-bold text-brand-blue">We Offer Services For Product Development Using Innovative Instruments</h3>
                    <p className="text-slate-500 font-light text-sm mt-1">Dedicated lines for hot melt extrusion, thin-film casting, and transdermal formulations.</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">1</div>
                      <div>
                        <h4 className="text-lg font-bold text-brand-blue">Thermofisher Pharma 11</h4>
                        <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                          Hot Melt Extrusion (HME) coupled with Twin Screw Granulation. Equipped with multi-die adapters for flexible solid dispersion processing.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">2</div>
                      <div>
                        <h4 className="text-lg font-bold text-brand-blue">Oral Dissolving Film (ODF) Formation</h4>
                        <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                          Precise film casting capabilities utilizing advanced casting methods and HME. <span className="font-semibold text-brand-teal">We proudly offer a 100% solvent-free formulation process</span>.
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-4 bg-slate-50/50 border border-slate-100 p-6 rounded-2xl hover:border-brand-teal/20 transition-all duration-300">
                      <div className="w-8 h-8 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center shrink-0 mt-1 font-bold text-sm">3</div>
                      <div>
                        <h4 className="text-lg font-bold text-brand-blue">Oral Films, Transdermal Patches &amp; Creams</h4>
                        <p className="text-slate-600 font-light text-xs md:text-sm mt-1 leading-relaxed">
                          Dedicated developmental parameters and tools tailored specifically for topical creams, transdermal delivery patches, and oral film strips.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="relative group overflow-hidden rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                    <img 
                      src={technicalProcessingInstruments} 
                      alt="Processing Instruments" 
                      className="w-full h-[320px] lg:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-85" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider">Research Instrumentation</p>
                      <h4 className="text-sm font-bold mt-0.5">Hot Melt Extruder &amp; ODF Casting</h4>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 3: Stability & Storage */}
            {activeTab === "stability" && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 animate-fade-in">
                <div className="lg:col-span-7 space-y-8">
                  <div className="border-b border-slate-100 pb-6">
                    <div className="inline-block text-[10px] font-bold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-2.5 py-1 rounded-full mb-3">
                      Stability Assurance
                    </div>
                    <h3 className="text-2xl font-bold text-brand-blue">We Assure Stable Formulations</h3>
                    <p className="text-slate-500 font-light text-sm mt-1">Accelerated condition, Zone IV B, and Real Time environmental mapping.</p>
                  </div>

                  {/* 3 Testing Conditions cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl text-center">
                      <span className="text-xl font-extrabold text-brand-teal block">40°C / 75% RH</span>
                      <h5 className="text-xs font-bold text-brand-blue mt-1">Accelerated Stability</h5>
                      <p className="text-slate-500 font-light text-[10px] mt-0.5">High stress environmental evaluation.</p>
                    </div>
                    <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl text-center">
                      <span className="text-xl font-extrabold text-brand-teal block">30°C / 75% RH</span>
                      <h5 className="text-xs font-bold text-brand-blue mt-1">Zone IV B Conditions</h5>
                      <p className="text-slate-500 font-light text-[10px] mt-0.5">Hot &amp; very humid climatic testing.</p>
                    </div>
                    <div className="bg-slate-50/50 border border-slate-100 p-4 rounded-2xl text-center">
                      <span className="text-xl font-extrabold text-brand-teal block">25°C / 60% RH</span>
                      <h5 className="text-xs font-bold text-brand-blue mt-1">Real Time Stability</h5>
                      <p className="text-slate-500 font-light text-[10px] mt-0.5">Standard storage condition tracking.</p>
                    </div>
                  </div>

                  {/* Technical specs banner */}
                  <div className="bg-brand-blue-deep text-white p-6 rounded-2xl grid grid-cols-1 md:grid-cols-3 gap-6 text-center md:text-left">
                    <div className="border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:pr-6">
                      <h5 className="text-xs font-bold text-brand-teal-light uppercase">Regulatory Compliance</h5>
                      <p className="text-[11px] font-semibold mt-1">Stability Chamber Complying with 21 CFR Guidelines</p>
                    </div>
                    <div className="border-b md:border-b-0 md:border-r border-white/10 pb-4 md:pb-0 md:px-6">
                      <h5 className="text-xs font-bold text-brand-teal-light uppercase">Capacity Size</h5>
                      <p className="text-[11px] font-semibold mt-1">Large Scale 1000 Ltr Capacity Environmental Storage</p>
                    </div>
                    <div className="md:pl-6">
                      <h5 className="text-xs font-bold text-brand-teal-light uppercase">Redundancy Assurance</h5>
                      <p className="text-[11px] font-semibold mt-1">Dual Compressor Configuration with Zero Breakdown Guarantee</p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="relative group overflow-hidden rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300">
                    <img 
                      src={technicalStabilityChambers} 
                      alt="Stability Testing Chambers" 
                      className="w-full h-[320px] lg:h-[420px] object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-85" />
                    <div className="absolute bottom-4 left-4 right-4 text-white">
                      <p className="text-[10px] font-semibold text-brand-teal uppercase tracking-wider">Environmental Storage</p>
                      <h4 className="text-sm font-bold mt-0.5">21 CFR Compliant Stability Chambers</h4>
                    </div>
                  </div>
                </div>
              </div>
            )}


          </div>
        </div>
      </section>

      {/* 3. Additional Support Section */}
      <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block text-xs font-semibold text-brand-teal uppercase tracking-widest bg-brand-teal/10 px-3 py-1 rounded-full mb-3">
            Facility Optimization
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-blue tracking-tight">
            Additionally, We Support Our Partners With
          </h2>
          <p className="text-slate-500 font-light mt-3">
            Specialized R&amp;D engineering to enhance yields, validate transfers, and modernize legacy pharmaceutical lines.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {additionalSupport.map((item, idx) => (
            <div 
              key={idx}
              className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <h4 className="text-lg font-bold text-brand-blue mb-2">{item.title}</h4>
                <p className="text-slate-600 font-light text-xs md:text-sm leading-relaxed">{item.desc}</p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-50 flex items-center text-xs text-brand-teal font-semibold">
                <span>Core Competency</span>
                <span className="w-1.5 h-1.5 rounded-full bg-brand-teal ml-1.5 animate-pulse" />
              </div>
            </div>
          ))}
          
          {/* Out-of-box CTA Card */}
          <div className="bg-gradient-to-br from-brand-blue to-brand-blue-light text-white rounded-2xl p-6 shadow-md flex flex-col justify-between">
            <div>
              <h4 className="text-lg font-bold mb-2">Need Dossier Support?</h4>
              <p className="text-slate-300 text-xs md:text-sm font-light leading-relaxed">
                Connect with our team to request dedicated facility runs, dossier development timelines, or continuous process integration.
              </p>
            </div>
            <Link 
              to="/ContactUs"
              state={{ from: "Inquiry: Technical Services" }}
              className="mt-6 w-full text-center inline-block px-5 py-2.5 text-xs md:text-sm font-semibold rounded-full text-brand-blue bg-white hover:bg-brand-teal hover:text-white transition-all duration-200"
            >
              Inquire About Technical Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
